import proxy from './axios';
import {
  fetchQuery,
  getQueryData,
  invalidateQuery,
  invalidateQueries,
  queryKeys,
  setQueryData,
  STALE_TIMES,
} from './queryCache';

export { queryKeys, STALE_TIMES, invalidateQuery, invalidateQueries, getQueryData, setQueryData };

export const fetchProblemsList = async () => {
  const { data } = await proxy.get('/problems');
  return Array.isArray(data) ? data : [];
};

export const fetchContestsList = async () => {
  const { data } = await proxy.get('/contests');
  return Array.isArray(data) ? data : [];
};

export const fetchTopSolvers = async () => {
  const { data } = await proxy.get('/users/top-solvers');
  return data?.toppers || [];
};

export const fetchLeaderboard = async () => {
  const { data } = await proxy.get('/users/leaderboard');
  return data?.leaderboard || [];
};

export const fetchProblemDetail = async (slug, isAdmin = false) => {
  const { data } = await proxy.get(`/problems${isAdmin ? '/admin' : ''}/${slug}`);
  return data;
};

export const fetchNotifications = async () => {
  const { data } = await proxy.get('/notifications', { params: { limit: 20 } });
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    unreadCount: data?.unreadCount ?? 0,
  };
};

export const fetchPrograms = async () => {
  const { data } = await proxy.get('/problems/my-programs');
  return data;
};

export const fetchSavedProblems = async (filter = 'all') => {
  const { data } = await proxy.get('/problems/saved', { params: { filter } });
  return Array.isArray(data?.items) ? data.items : [];
};

export const loadProblems = (opts) =>
  fetchQuery(queryKeys.problems, fetchProblemsList, {
    staleTime: STALE_TIMES.problems,
    ...opts,
  });

export const loadContests = (opts) =>
  fetchQuery(queryKeys.contests, fetchContestsList, {
    staleTime: STALE_TIMES.contests,
    ...opts,
  });

export const loadTopSolvers = (opts) =>
  fetchQuery(queryKeys.topSolvers, fetchTopSolvers, {
    staleTime: STALE_TIMES.topSolvers,
    ...opts,
  });

export const loadLeaderboard = (opts) =>
  fetchQuery(queryKeys.leaderboard, fetchLeaderboard, {
    staleTime: STALE_TIMES.leaderboard,
    ...opts,
  });

export const loadProblem = (slug, isAdmin = false, opts) =>
  fetchQuery(queryKeys.problem(slug, isAdmin), () => fetchProblemDetail(slug, isAdmin), {
    staleTime: STALE_TIMES.problem,
    ...opts,
  });

export const loadNotifications = (opts) =>
  fetchQuery(queryKeys.notifications, fetchNotifications, {
    staleTime: STALE_TIMES.notifications,
    ...opts,
  });

export const loadPrograms = (opts) =>
  fetchQuery(queryKeys.programs, fetchPrograms, {
    staleTime: STALE_TIMES.programs,
    ...opts,
  });

/** Client-side search when the problems list is already cached. */
export const searchProblemsFromCache = (query, limit = 12) => {
  const problems = getQueryData(queryKeys.problems);
  if (!Array.isArray(problems) || !query || query.length < 2) return null;

  const q = query.toLowerCase();
  const matches = problems.filter((p) => {
    const title = (p.title || '').toLowerCase();
    const slug = (p.slug || '').toLowerCase();
    const topics = (p.topics || []).some((t) => String(t).toLowerCase().includes(q));
    return title.includes(q) || slug.includes(q) || topics;
  });

  return matches.slice(0, limit);
};

/** After submit/like — refresh problem list + detail caches. */
export const invalidateProblemCaches = (slug) => {
  invalidateQuery(queryKeys.problems);
  if (slug) invalidateQuery(queryKeys.problem(slug, false));
  invalidateQuery(queryKeys.topSolvers);
  invalidateQuery(queryKeys.leaderboard);
};

/**
 * Patches the given fields onto one problem across every cache that embeds
 * per-user problem state (`/problems`, `/problems/my-programs` — Practice
 * Track — and the single-problem detail cache). All three are populated by
 * the same backend getAllProblems()/getProblemBySlug() shape, so a field
 * patched on one applies cleanly to the others. Missing any of these is
 * exactly what caused bookmarks/notes/solved to show correctly on one page
 * but not another until the 5min stale time happened to expire.
 */
const patchProblemFieldsInCache = (slug, fields) => {
  [queryKeys.problems, queryKeys.programs].forEach((key) => {
    const list = getQueryData(key);
    if (Array.isArray(list)) {
      setQueryData(
        key,
        list.map((p) => (p.slug === slug ? { ...p, ...fields } : p)),
      );
    }
  });
  const detail = getQueryData(queryKeys.problem(slug, false));
  if (detail) {
    setQueryData(queryKeys.problem(slug, false), { ...detail, ...fields });
  }
};

export const patchProblemSolvedInCache = (slug) => {
  patchProblemFieldsInCache(slug, { isSolved: true, isAttempted: true });
};

/** Any submission (accepted or not) counts as "attempted" — call regardless of verdict. */
export const patchProblemAttemptedInCache = (slug) => {
  patchProblemFieldsInCache(slug, { isAttempted: true });
};

export const patchProblemBookmarkInCache = (slug, isBookmarked) => {
  patchProblemFieldsInCache(slug, { isBookmarked: !!isBookmarked });
};

export const patchProblemNoteInCache = (slug, note) => {
  patchProblemFieldsInCache(slug, { hasNote: !!note });
};
